/* eslint-disable react-hooks/exhaustive-deps */
import { createContext, useContext, useCallback, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

// Contexts
import { UserDetailsContext } from 'context/userDetailsContext';
import { useNavbarContext } from 'context/NavbarContext';
import { WebSocketConnectionContext } from 'context/WebSocketConnectionContext';

// Hooks
import { useDebounce } from 'hooks/useDebounce';
import useGetFiles from '../hooks/api/useGetFiles';
import useGetTags from '../hooks/api/useGetTags';
import useHandleFilterChange from '../hooks/ui/useHandleFilterChange';
import useBreadCrumbs from '../hooks/ui/useBreadCrumbs';
import useHandleSelectedRows from '../hooks/ui/useHandleSelectedRows';
import useFileManagerModalsState from './hooks/useFileManagerModalsState';
import useFileManagerEffects from './hooks/useFileManagerEffects';

// Utils & Config
import { getDefaultFilter } from './defaultFilters';
import {
  createOnResetFilter,
  createGetGlobalTags,
  createHandleSummarizeFile,
  createHandleShowCreateEvent,
  createHandleShowSummaryModal,
} from './handlers';

const FileManagerContext = createContext({});

export const useFileManagerContext = () => {
  const context = useContext(FileManagerContext);
  if (context === undefined) {
    throw new Error('useFileManagerContext must be used within FileManagerContextProvider');
  }
  return context;
};

export const FileManagerContextProvider = (props) => {
  const {
    isGlobalFileManager,
    isCaseFileManager,
    isLeadFileManager,
    isClientFileManager,
    isFirmDocuments,
    caseDetails,
    isClosed,
    onMountUnMount,
    onOperationBreak,
    casePage,
  } = props;

  // -- Contexts & Core Hooks --
  const location = useLocation();
  const queryClient = useQueryClient();
  const userContext = useContext(UserDetailsContext);
  const websocketContext = useContext(WebSocketConnectionContext);
  const { readClientNotifications, notificationCounts } = useNavbarContext();

  // -- Filters & View State --
  const userAccessLevel = userContext?.access_level;
  const userClientSelectedCase = userContext?.clientSelectedCase;
  const getDefaultFilterFn = useCallback(
    () => getDefaultFilter(location, userContext, isCaseFileManager, isClientFileManager, isLeadFileManager),
    [location, userAccessLevel, userClientSelectedCase, isCaseFileManager, isClientFileManager, isLeadFileManager]
  );

  const { onTagChange, onFilterChange, fileManagerFilters, setFileManagerFilters } = useHandleFilterChange(getDefaultFilterFn);

  const [currentView, setCurrentView] = useState('list');
  const [query, setQuery] = useState('');
  const queryDebounced = useDebounce(query, 400);

  // -- Data Fetching & Core Logic --
  const {
    files,
    getFiles,
    pageLimit,
    setPageLimit,
    onNextPageClick,
    onPrevPageClick,
    paginationToken,
    previousToken,
    listRequestPaginationToken: listRequestPaginationTokenRaw,
    refetch,
    refetchWithFolder,
  } = useGetFiles(isGlobalFileManager, isCaseFileManager, isLeadFileManager, isClientFileManager, userContext, {
    fileManagerFilters,
    caseDetails,
    isFirmDocuments,
    currentView,
    query: queryDebounced,
  });

  const { getFileTags, fullTags, isLoading: isTagsLoading } = useGetTags(caseDetails?.case_id ?? '');
  const { breadCrumbs, setBreadCrumbs, handleBreadCrumbs, filterBreadCrumbs } = useBreadCrumbs();
  const { selectedRows, selectAll, handleSingleCheckboxClick, handleSelectAllCheckboxClick, setSelectedRows } = useHandleSelectedRows();

  // -- Modals & Global UI State --
  const modalState = useFileManagerModalsState();
  const {
    overrideFiles,
    setOverrideFiles,
    isContentSearchActive,
    setIsContentSearchActive,
    selectedItem,
    setSelectedItem,
    fileName,
    setFileName,
    deletePermission,
    setDeletePermission,
    versionFileUpload,
    setVersionFileUpload,
    previewList,
    setPreviewList,
    selectedPreviewItem,
    setSelectedPreviewItem,
    setShowPreview,
    globalTags,
    setGlobalTags,
    setShowSummarizeModal,
    setShowConfigureSummaryModal,
    summaryData,
    setSummaryData,
    summaryVersions,
    setSummaryVersions,
    summaryGenerationStatus,
    setSummaryGenerationStatus,
    setShowCreateEventModal,
    courtDateMsg,
    setCourtDateMsg,
    resetContentSearch,
    modals,
  } = modalState;

  // -- Effects --
  useFileManagerEffects({
    location,
    userContext,
    isCaseFileManager,
    isLeadFileManager,
    isClientFileManager,
    casePage,
    notificationCounts,
    readClientNotifications,
    websocketContext,
    files,
    setFileManagerFilters,
    setBreadCrumbs,
    setShowPreview,
    setSelectedItem,
    setSelectedPreviewItem,
    setPreviewList,
    setDeletePermission,
    setSummaryGenerationStatus,
  });

  // -- Handlers & Actions --
  const handleGetFiles = useCallback(
    (pageLimitArg, folder_id) => {
      queryClient.invalidateQueries({ queryKey: ['file-manager', 'list'] });
      if (folder_id != null && folder_id !== '') {
        refetchWithFolder(folder_id);
      } else {
        refetch();
      }
    },
    [queryClient, refetch, refetchWithFolder]
  );

  const onResetFilter = useMemo(
    () =>
      createOnResetFilter({
        setFileManagerFilters,
        setQuery,
        setBreadCrumbs,
        userContext,
        isCaseFileManager,
        isClientFileManager,
        isLeadFileManager,
        isFirmDocuments,
      }),
    [
      setFileManagerFilters,
      setQuery,
      setBreadCrumbs,
      userContext,
      isCaseFileManager,
      isClientFileManager,
      isLeadFileManager,
      isFirmDocuments,
    ]
  );

  const getGlobalTags = useMemo(() => createGetGlobalTags({ setGlobalTags }), [setGlobalTags]);

  const handleSummarizeFile = useMemo(
    () => createHandleSummarizeFile({ setSelectedItem, setSummaryGenerationStatus }),
    [setSelectedItem, setSummaryGenerationStatus]
  );

  const handleShowSummaryModal = useMemo(
    () =>
      createHandleShowSummaryModal({
        setSelectedItem,
        setSummaryData,
        setSummaryVersions,
        setShowSummarizeModal,
        websocketContext,
      }),
    [setSelectedItem, setSummaryData, setSummaryVersions, setShowSummarizeModal, websocketContext]
  );

  const handleShowCreateEvent = useMemo(
    () => createHandleShowCreateEvent({ setShowCreateEventModal, setSelectedItem, setCourtDateMsg }),
    [setShowCreateEventModal, setSelectedItem, setCourtDateMsg]
  );

  const handleGenerateConfigureSummary = useCallback(
    (config) => {
      const { format, length, rowData } = config;
      setShowConfigureSummaryModal(false);
      const data = {
        file_path: rowData?.file_path,
        file_name: rowData?.file_name,
        file_id: rowData?.file_id || rowData?.object_id || rowData?.sk?.split('#')?.[1],
        version_id: rowData?.version_id,
        sk: rowData?.sk,
        regenerate: 1,
        summary_option: format,
        summary_percentage: length,
        timezone: userContext?.userDetails?.timezone,
      };
      handleSummarizeFile(rowData, data);
    },
    [setShowConfigureSummaryModal, userContext?.userDetails?.timezone, handleSummarizeFile]
  );

  const handleOpenConfigureSummaryModal = useCallback(
    (rowData) => {
      setSelectedItem(rowData);
      setShowConfigureSummaryModal(true);
    },
    [setSelectedItem, setShowConfigureSummaryModal]
  );

  const hideCreateEventModal = useCallback(() => {
    setShowCreateEventModal(false);
    setCourtDateMsg();
  }, [setShowCreateEventModal]);

  const visibleFiles = overrideFiles ?? files;
  const listRequestPaginationToken = overrideFiles ? '' : listRequestPaginationTokenRaw;

  // -- Context Payload --
  const contextPayload = useMemo(
    () => ({
      // Scope & Config
      isGlobalFileManager,
      isCaseFileManager,
      isLeadFileManager,
      isClientFileManager,
      isFirmDocuments,
      caseDetails,
      isClosed,
      onMountUnMount,
      onOperationBreak,

      // Filter & Query
      fileManagerFilters,
      setFileManagerFilters,
      onFilterChange,
      onResetFilter,
      onTagChange,
      query,
      setQuery,

      // View & Navigation
      currentView,
      setCurrentView,
      breadCrumbs,
      setBreadCrumbs,
      handleBreadCrumbs,
      filterBreadCrumbs,

      // Files Data
      files: visibleFiles,
      getFiles,
      handleGetFiles,
      pageLimit,
      setPageLimit,
      paginationToken,
      previousToken,
      listRequestPaginationToken,

      // Selection
      selectedRows,
      selectAll,
      handleSingleCheckboxClick,
      handleSelectAllCheckboxClick,
      setSelectedRows,

      // Preview & Modals
      selectedItem,
      setSelectedItem,
      fileName,
      setFileName,
      deletePermission,
      modals,
      versionFileUpload,
      setVersionFileUpload,
      previewList,
      setPreviewList,
      selectedPreviewItem,
      setSelectedPreviewItem,
      onNextPageClick,
      onPrevPageClick,

      // Tags
      fullTags,
      globalTags,
      getGlobalTags,
      getFileTags,
      isTagsLoading,

      // Summary
      handleSummarizeFile,
      handleGenerateConfigureSummary,
      handleShowSummaryModal,
      handleOpenConfigureSummaryModal,
      summaryData,
      setSummaryData,
      summaryVersions,
      summaryGenerationStatus,

      // Events
      handleShowCreateEvent,
      hideCreateEventModal,
      courtDateMsg,

      // Search & Misc
      isContentSearchActive,
      setIsContentSearchActive,
      setOverrideFiles,
      resetContentSearch,
    }),
    [
      isGlobalFileManager,
      isCaseFileManager,
      isLeadFileManager,
      isClientFileManager,
      isFirmDocuments,
      fileManagerFilters,
      onFilterChange,
      onResetFilter,
      currentView,
      visibleFiles,
      getFiles,
      handleGetFiles,
      pageLimit,
      selectedRows,
      selectAll,
      breadCrumbs,
      selectedItem,
      fileName,
      caseDetails,
      isClosed,
      deletePermission,
      modals,
      versionFileUpload,
      previewList,
      selectedPreviewItem,
      onNextPageClick,
      onPrevPageClick,
      paginationToken,
      previousToken,
      listRequestPaginationToken,
      fullTags,
      query,
      onMountUnMount,
      onOperationBreak,
      globalTags,
      getGlobalTags,
      getFileTags,
      handleSummarizeFile,
      handleGenerateConfigureSummary,
      handleShowSummaryModal,
      handleOpenConfigureSummaryModal,
      summaryData,
      summaryVersions,
      summaryGenerationStatus,
      handleShowCreateEvent,
      hideCreateEventModal,
      courtDateMsg,
      isTagsLoading,
      isContentSearchActive,
      resetContentSearch,
    ]
  );

  return <FileManagerContext.Provider value={contextPayload} {...props} />;
};
